import { useEffect, useRef, useState } from 'react';

export enum RefreshStateType {
  DRAGGING = 'dragging',
  IDLE = 'idle',
  REFRESHING = 'refreshing',
  REFRESHING_COMPLETE = 'refreshing_complete',
}

interface UsePullToRefreshStateProps {
  /**
   * Whether the refresh is currently in progress (from refresh hooks)
   */
  isRefreshing: boolean;
  /**
   * Whether the user is currently dragging the scroll
   */
  isScrollDragging: boolean;
}

interface UsePullToRefreshStateReturn {
  /**
   * Whether the loader component should be enabled/visible
   */
  isEnabled: boolean;
  /**
   * Whether to show the loading dots (instead of circle)
   */
  showDots: boolean;
  /**
   * Current state of the refresh flow
   */
  state: RefreshStateType;
}

/**
 * State machine for pull-to-refresh flow
 *
 * States:
 * - idle: No interaction, nothing showing
 * - dragging: User is dragging, show circle with progress
 * - refreshing: Refresh started, show dots, keep showing until refresh completes AND scroll stops
 * - refreshing_complete: Refresh done but user still scrolling, keep dots until scroll stops
 */
export const usePullToRefreshState = ({
  isScrollDragging,
  isRefreshing,
}: UsePullToRefreshStateProps): UsePullToRefreshStateReturn => {
  const [state, setState] = useState<RefreshStateType>(RefreshStateType.IDLE);
  const refreshStartedRef = useRef(false);

  // Track when refresh starts
  useEffect(() => {
    if (isRefreshing && !refreshStartedRef.current) {
      refreshStartedRef.current = true;
    }
  }, [isRefreshing]);

  useEffect(() => {
    if (isRefreshing) {
      setState(RefreshStateType.REFRESHING);
    } else if (refreshStartedRef.current && isScrollDragging) {
      setState(RefreshStateType.REFRESHING_COMPLETE);
    } else if (refreshStartedRef.current && !isScrollDragging) {
      setState(RefreshStateType.IDLE);
      refreshStartedRef.current = false;
    } else if (isScrollDragging) {
      setState(RefreshStateType.DRAGGING);
    } else {
      setState(RefreshStateType.IDLE);
      refreshStartedRef.current = false;
    }
  }, [isScrollDragging, isRefreshing]);

  const isEnabled = state !== 'idle';

  const showDots = state === 'refreshing' || state === 'refreshing_complete';

  return {
    isEnabled,
    showDots,
    state,
  };
};
